import * as React from 'react';
import Cookies from 'js-cookie';
import Grid from '@material-ui/core/Grid';
import TranslateIcon from '@material-ui/icons/Translate';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { changePreferredCultureCode } from '../helpers/changePreferredCultureCode';
import { LanguageSwitcherParent } from '../types';
import {
  additionalLanguageOptions,
  displayNameTypes,
  languageOptions,
  languageSwitcherParentTypes,
  localeCookie,
  mvcAppLocaleCookie
} from '../constants';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { StylesProvider, createGenerateClassName } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { createIntl } from '@formatjs/intl';
import { useFeature } from 'flagged';

type Props = {
  typography?: string;
  parent?: LanguageSwitcherParent;
  userId?: string;
  iconVisible?: boolean;
};

const generateClassName = createGenerateClassName({
  seed: 'aion-ui-i18next',
  productionPrefix: 'aion-ui-i18next'
});

const useHeaderStyles = makeStyles((theme: Theme) => ({
  languageSwitcherContainer: {
    marginRight: 0,
    marginLeft: theme.spacing(4),
    justifyContent: 'flex-end',
    alignItems: 'center',
    display: 'flex',
    width: 'auto'
  },
  select: {
    color: theme.palette.common.white,
    fontSize: theme.typography.pxToRem(14),
    '&::before, &::after': {
      display: 'none'
    }
  },
  selectEl: {
    paddingBottom: '0.25rem',
    '&:focus': {
      backgroundColor: 'transparent'
    }
  },
  icon: {
    fill: theme.palette.common.white
  },
  languageIcon: {
    fill: theme.palette.common.white
  },
  iconContainer: {
    display: 'flex',
    marginRight: '0.25rem'
  },
  languageText: {
    textTransform: 'capitalize'
  }
}));

const useFooterStyles = makeStyles((theme: Theme) => ({
  languageSwitcherContainer: {
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    justifyContent: 'center',
    display: 'flex',
    width: 'auto'
  },
  select: {
    fontSize: theme.typography.pxToRem(14),
    '&::before, &::after': {
      display: 'none'
    }
  },
  selectEl: {
    paddingBottom: '0.25rem',
    '&:focus': {
      backgroundColor: 'transparent'
    }
  },
  icon: {
    fill: theme.palette.text.secondary
  },
  languageIcon: {
    fill: theme.palette.text.secondary
  },
  iconContainer: {
    display: 'flex',
    marginRight: '0.25rem'
  },
  languageText: {
    textTransform: 'capitalize'
  }
}));

const useRegistrationStyles = makeStyles((theme: Theme) => ({
  languageSwitcherContainer: {
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    justifyContent: 'center',
    display: 'flex',
    width: 'auto'
  },
  select: {
    color: theme.palette.common.white,
    fontSize: theme.typography.pxToRem(14),
    '&::before, &::after': {
      display: 'none'
    }
  },
  selectEl: {
    paddingBottom: '0.25rem',
    '&:focus': {
      backgroundColor: 'transparent'
    }
  },
  icon: {
    fill: theme.palette.common.white
  },
  languageIcon: {
    fill: theme.palette.common.white
  },
  iconContainer: {
    display: 'flex',
    marginRight: '0.25rem'
  },
  languageText: {
    textTransform: 'capitalize'
  }
}));

export const LanguageSwitcherComponent: React.FC<Props> = ({
  parent = languageSwitcherParentTypes.header,
  userId,
  iconVisible = true
}) => {
  const { i18n } = useTranslation();
  const header = useHeaderStyles();
  const footer = useFooterStyles();
  const registration = useRegistrationStyles();
  const isExtendedLocalesEnabled = useFeature('EnableExtendedLocales');
  const classes = {
    header,
    footer,
    registration
  }[parent];

  const onLanguageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const lng = e.target.value;
    const legacyAppPath = 'SSQV4';

    // Determine cookie domain for cross-subdomain sharing
    // For localhost, don't set domain; for other hosts, use root domain with leading dot
    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const cookieOptions: Cookies.CookieAttributes = isLocalhost
      ? {}
      : {
          domain:
            '.' +
            hostname
              .split('.')
              .slice(-2)
              .join('.')
        };

    Cookies.set(localeCookie, lng, cookieOptions);
    // setting additional cookie to synchronize locale with mvc applications
    Cookies.set(mvcAppLocaleCookie, `c=${lng}|uic=${lng}`, cookieOptions);

    if (userId) {
      await changePreferredCultureCode(userId, lng);
    }

    if (window.location.pathname.toUpperCase().includes(legacyAppPath)) {
      window.location.reload();
    } else {
      i18n.changeLanguage(lng);
    }
  };

  return (
    <StylesProvider generateClassName={generateClassName}>
      <Grid className={classes.languageSwitcherContainer}>
        {iconVisible && (
          <Grid className={classes.iconContainer}>
            <TranslateIcon className={classes.languageIcon} />
          </Grid>
        )}
        <Grid item>
          <Select
            className={classes.select}
            name="language-switcher"
            onChange={onLanguageChange}
            value={i18n.language}
            inputProps={{
              classes: {
                icon: classes.icon,
                select: classes.selectEl
              }
            }}
          >
            {[...languageOptions, ...(isExtendedLocalesEnabled ? additionalLanguageOptions : [])].map(
              ({ value, languageCode, countryCode }) => {
                const intl = createIntl({
                  locale: value,
                  messages: {}
                });
                return (
                  <MenuItem value={value} key={value}>
                    <span className={classes.languageText}>
                      {intl.formatDisplayName(languageCode, { type: displayNameTypes.language })}
                    </span>
                    <span>&nbsp;({intl.formatDisplayName(countryCode, { type: displayNameTypes.region })})</span>
                  </MenuItem>
                );
              }
            )}
          </Select>
        </Grid>
      </Grid>
    </StylesProvider>
  );
};
